import { observable, computed, makeAutoObservable } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { TermsModelType } from 'src_common/common/websocket2/modelsApi/Market';
import { FormConcept } from './FormConcept';
import { EventId, MarketId, SelectionId } from 'src_common/common/websocket2/id/WebsocketId';

interface PlaceReductionsType {
    eachWayTerms: {
        places: number;
        reduction: string;
    };
    placeReduction: string;
}
interface DataSaveType {
    type: string;
    place: string | undefined;
    winReduction: string | undefined;
    placeReductions: Array<PlaceReductionsType>;
    isDraft: boolean;
}

interface PlaceReductionsItemType {
    eachWayTerms: {
        places: number;
        reduction: string;
    };
    placeReduction: string | undefined | null;
}

export class EventResultsSettingsSelectionState {
    public readonly spInput: FormConcept<string>;
    public readonly resultInput: FormConcept<string>;
    public readonly placeInput: FormConcept<string>;
    public readonly winReductionInput: FormConcept<string>;
    public readonly reductionInputMap: MobxMapAutoNew<number, MobxMapAutoNew<string, FormConcept<string>>>; //places: number; reduction: string;

    @observable public loading: string | null = null;
    @observable public isSpInputError: boolean = false;
    @observable private unlockFlag: boolean = false;

    public constructor(
        private readonly appState: AppState,
        public readonly eventId: EventId,
        public readonly marketId: MarketId,
        public readonly selectionId: SelectionId
    ) {
        this.spInput = new FormConcept(() => this.selectionModel?.spPrice?.f ?? '');

        this.resultInput = new FormConcept(() => this.selectionModel?.resultType ?? '');

        this.placeInput = new FormConcept(() => this.selectionModel?.resultPlace?.toString() ?? '');

        this.winReductionInput = new FormConcept(() => this.selectionModel?.getData().result?.winReduction ?? '');

        this.reductionInputMap = new MobxMapAutoNew(
            (places: number) =>
                new MobxMapAutoNew(
                    (reduction: string) =>
                        new FormConcept(() => {
                            for (const item of this.eachWaySelection) {
                                if (item.eachWayTerms.places === places && item.eachWayTerms.reduction === reduction) {
                                    return item.placeReduction ?? '';
                                }
                            }

                            return '';
                        })
                )
        );

        makeAutoObservable(this);
    }

    private get marketModel(): MarketModel | null {
        return this.marketId.getModel();
    }

    public get selectionModel(): SelectionModel | null {
        return this.selectionId.getModel();
    }

    @computed public get isSelectionSettled(): boolean {
        if (this.selectionModel === null) {
            return false;
        }
        const result = this.selectionModel.getRawData().result;

        if (result === null || result === undefined) {
            return false;
        }

        return result.isDraft === false;
    }

    public get isLost(): boolean {
        return this.resultInput.value === 'lost';
    }

    public get isVoid(): boolean {
        return this.resultInput.value === 'void';
    }

    public get isPlace(): boolean {
        return this.resultInput.value === 'place';
    }

    public get unlock(): boolean {
        return this.unlockFlag;
    }

    private get eachWaySelection(): Array<PlaceReductionsItemType> {
        return this.selectionModel?.getData().result?.placeReductions ?? [];
    }

    public get eachWayMarket(): Array<TermsModelType> {
        return this.marketModel?.eachWay?.terms ?? [];
    }

    public setUnlock = (newValue: boolean): void => {
        this.unlockFlag = newValue;
    };

    private placeReductionsWasChange(): boolean {
        for (const terms of this.eachWay) {
            const inputplaceReduction = this.reductionInputMap.get(terms.places).get(terms.reduction);
            if (inputplaceReduction.wasChange) {
                return true;
            }
        }

        return false;
    }

    private isSpChanged(): boolean {
        return this.spInput.wasChange;
    }

    private isSelectionChanged(): boolean {
        return (
            this.resultInput.wasChange ||
            this.placeInput.wasChange ||
            this.winReductionInput.wasChange ||
            this.placeReductionsWasChange()
        );
    }

    public getState(): string | null {
        return this.selectionModel?.state ?? null;
    }

    private getMarketSp(): boolean {
        return this.marketId.getModel()?.sp ?? false;
    }

    public getSelectionStatus(): 'to-be-saved' | 'to-be-settled' | 'to-be-populated' {
        if (this.isSpChanged() || this.isSelectionChanged()) {
            return 'to-be-saved';
        } else if (this.resultInput.value === 'void' || this.resultInput.value === 'lost') {
            return 'to-be-settled';
        } else if (
            this.resultInput.value === '' ||
            this.resultInput.value === 'unresult' ||
            (this.getMarketSp() && this.spInput.value === '')
        ) {
            return 'to-be-populated';
        } else {
            return 'to-be-settled';
        }
    }

    public resetUserValues = (): void => {
        this.setUnlock(false);
        this.spInput.reset();
        this.resultInput.reset();
        this.placeInput.reset();
        this.winReductionInput.reset();

        for (const terms of this.eachWay) {
            const inputplaceReduction = this.reductionInputMap.get(terms.places).get(terms.reduction);
            inputplaceReduction.reset();
        }
    };

    private validateSpInput(): void {
        const isSpInputEmpty = this.spInput.value === '';
        const isSpRequired = !this.isLost && !this.isVoid;
        if (this.getMarketSp() && isSpInputEmpty && isSpRequired) {
            return this.setSpInputError(true);
        } else {
            this.setSpInputError(false);
        }
    }

    private setSpInputError = (flag: boolean): void => {
        this.isSpInputError = flag;
    };

    public settle = async (): Promise<void> => {
        if (this.loading !== null) {
            return;
        }

        this.validateSpInput();
        this.loading = 'Settling';

        try {
            const result = await this.appState.sdkStaff.selection_settle({
                requestBody: {
                    event_id: this.eventId.toOldId(),
                    market_id: this.marketId.toOldId(),
                    selection_id: this.selectionId.toOldId(),
                },
            });

            if (this.appState.reduxAction.addErrorEventChange(result)) {
                this.loading = null;
                return;
            }
        } catch (error) {
            console.error(error);
            this.loading = null;
            return;
        }

        this.appState.sdkStaff.triggerUpdateMarket(this.marketId).catch((error) => {
            console.error(error);
        });

        await timeout(1500);
        this.resetUserValues();
        this.loading = null;
    };

    public unsettle = async (): Promise<void> => {
        if (this.loading !== null) {
            return;
        }

        this.loading = 'Unsettling';

        try {
            const result = await this.appState.sdkStaff.selection_unsettle({
                requestBody: {
                    event_id: this.eventId.toOldId(),
                    market_id: this.marketId.toOldId(),
                    selection_id: this.selectionId.toOldId(),
                },
            });

            if (this.appState.reduxAction.addErrorEventChange(result)) {
                this.loading = null;
                return;
            }
        } catch (error) {
            console.error(error);
            this.loading = null;
            return;
        }

        this.appState.sdkStaff.triggerUpdateMarket(this.marketId).catch((error) => {
            console.error(error);
        });

        await timeout(1500);
        this.resetUserValues();

        this.loading = null;
    };

    private get eachWay(): Array<TermsModelType> {
        const eachWay = this.marketModel?.eachWay?.terms ?? [];
        return eachWay;
    }

    private getPlaceReductions = (): Array<PlaceReductionsType> => {
        const out = [];

        for (const terms of this.eachWay) {
            const placeReduction = this.reductionInputMap.get(terms.places).get(terms.reduction).value;

            if (placeReduction !== '') {
                out.push({
                    eachWayTerms: terms,
                    placeReduction,
                });
            }
        }

        return out;
    };

    public getDataToSave = (isDraft: boolean): DataSaveType => {
        return {
            type: this.resultInput.value,
            place: this.placeInput.value === '' ? undefined : this.placeInput.value,
            winReduction: this.winReductionInput.value === '' ? undefined : this.winReductionInput.value,
            placeReductions: this.getPlaceReductions(),
            isDraft,
        };
    };

    public savePrice = async (): Promise<void> => {
        if (this.isSpChanged()) {
            this.setSpInputError(false);

            await this.appState.sdkStaff.updateSelectionPrice({
                event_id: this.eventId.toOldId(),
                market_id: this.marketId.toOldId(),
                selection_id: this.selectionId.toOldId(),
                requestBody: {
                    price: this.spInput.value,
                    priceType: 'sp',
                },
            });

            this.spInput.reset();
        }
    };

    public save = async (): Promise<void> => {
        if (this.loading !== null) {
            return;
        }

        this.loading = 'Saving';

        try {
            await this.savePrice();
        } catch (error) {
            console.error(error);
            this.loading = null;
            return;
        }

        if (this.isSelectionChanged()) {
            try {
                const result = await this.appState.sdkStaff.selection_result({
                    requestBody: {
                        event_id: this.eventId.toOldId(),
                        market_id: this.marketId.toOldId(),
                        selection_id: this.selectionId.toOldId(),
                        data: this.getDataToSave(true),
                    },
                });
                if (this.appState.reduxAction.addErrorEventChange(result)) {
                    this.loading = null;
                    return;
                }
            } catch (error) {
                console.error(error);
                this.loading = null;
                return;
            }
        }

        this.appState.sdkStaff.triggerUpdateMarket(this.marketId).catch((error) => {
            console.error(error);
        });

        await timeout(1500);
        this.resetUserValues();

        this.loading = null;
    };
}
